/**
 * Generic observer manager implementing the Observer pattern
 * Can be used for any type of event/observer pattern across the application
 *
 * @template TEvent The type of event that observers will receive
 *
 * @example
 * ```typescript
 * interface CustomEvent {
 *   type: string;
 *   data: any;
 * }
 *
 * const manager = new ObserverManager<CustomEvent>();
 *
 * const unsubscribe = manager.subscribe((event) => {
 *   console.log('Event received:', event);
 * });
 *
 * manager.notify({ type: 'update', data: { value: 42 } });
 *
 * unsubscribe(); // Stop receiving events
 * ```
 */
export class ObserverManager<TEvent> {
  private readonly observers: Set<(event: TEvent) => void> = new Set();

  /**
   * Subscribe to events
   * @param observer Callback function to be called on events
   * @returns Unsubscribe function
   */
  public subscribe(observer: (event: TEvent) => void): () => void {
    this.observers.add(observer);

    // Return unsubscribe function
    return () => {
      this.observers.delete(observer);
    };
  }

  /**
   * Unsubscribe from events
   * @param observer Observer to remove
   */
  public unsubscribe(observer: (event: TEvent) => void): void {
    this.observers.delete(observer);
  }

  /**
   * Notify all observers of an event
   * @param event Event to broadcast
   */
  public notify(event: TEvent): void {
    // Create a copy of observers set to avoid issues if observers modify the set during iteration
    const observersCopy = Array.from(this.observers);

    for (const observer of observersCopy) {
      try {
        observer(event);
      } catch (error) {
        // Log error but don't break notification chain
        console.error('Error in observer:', error);
      }
    }
  }

  /**
   * Clear all observers
   */
  public clear(): void {
    this.observers.clear();
  }

  /**
   * Get the number of active observers
   */
  public get count(): number {
    return this.observers.size;
  }
}
